import {
  ArrowClockwiseIcon,
  ArrowUpRightIcon,
  BugIcon,
  CheckIcon,
  DoorIcon,
  LockIcon,
  QuestionIcon,
  ShieldCheckIcon,
  UserIcon,
  WarningCircleIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { P, match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button.tsx'
import { PriorityCompositionBar } from '../../components/composition-bar/priority-composition-bar.tsx'
import { DropdownFilter, type Filter } from '../../components/dropdown-filter.tsx'
import { LinkedFindingChip } from '../../components/findings/linked-finding-chip.tsx'
import { RenderedMarkdown } from '../../components/rendered-markdown.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import {
  invalidateGetRepositoryFindingOverlays,
  useFindingOverlaysStream,
  useGetRepositoryFindingOverlays,
  useGetRepositoryFindingOverlaysContent,
  useGetRepositoryThreatModel,
  usePostRepositoryFindingOverlays,
} from '../../utils/api-client/api-hooks.ts'
import {
  FindingOverlayPhaseSchema,
  isInFlightGenerationStatus,
  type FindingBackreferenceLinks,
  type FindingOverlayCluster,
  type FindingOverlayPhase,
  type FindingOverlayPriority,
  type FindingOverlayResponse,
  type FindingOverlaySummary,
  type FindingOverlayTier,
} from '../../utils/api-client/api-schemas.ts'
import { formatDate, formatRelativeTime } from '../../utils/date-utils.ts'
import { scanIdFromFindingBackreference } from '../../utils/finding-utils.ts'
import { joinMetaSegments, pluralize } from '../../utils/text-utils.ts'
import * as styles from './finding-overlays-tab.css.ts'
import { PhaseStepper } from './phase-stepper.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.hooks.tsx'

// `_links` are REST API paths, not UI routes, so the chip gets the app route for
// the finding's scan (`/scans/{scanId}`) rather than the JSON endpoint. The scan id
// is read out by the shared backreference helper, which the exploit-chains tab uses
// too — the href shape is one contract, so it is parsed in one place.
const scanRouteFromLinks = (links: FindingBackreferenceLinks | undefined): string | undefined => {
  const scanId = scanIdFromFindingBackreference(links)
  return scanId ? Routes.ScanDetailsPage.createPath({ scanId }) : undefined
}

// `unknown` is a fallback the backend reports when it can't determine the live phase; it is not a
// real generation step, so it is excluded from the stepper's ordered phases. Mirrors the threat-model
// tab, which reconciles the same `unknown`-bearing phase enum against the same stepper.
type SupportedFindingOverlayPhase = Exclude<FindingOverlayPhase, 'unknown'>

const FINDING_OVERLAYS_PHASES = FindingOverlayPhaseSchema.options.filter(
  (phase): phase is SupportedFindingOverlayPhase => phase !== 'unknown'
)

// An indeterminate phase renders as the first step rather than mis-indexing the stepper on a value
// it has no position for.
const toSupportedPhase = (phase: FindingOverlayPhase): SupportedFindingOverlayPhase =>
  phase === 'unknown' ? 'queued' : phase

const PHASE_LABELS = {
  queued: 'Queued',
  analyzing: 'Analyzing findings',
} satisfies Record<SupportedFindingOverlayPhase, string>

// Tier ordering for priority list rendering (critical → low), with the indeterminate tier last: a
// finding the agent ranked but could not tier still belongs on the page, below the ones it could.
const TIER_ORDER = ['critical', 'high', 'medium', 'low', 'unknown'] as const satisfies readonly FindingOverlayTier[]

const TIER_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unknown: 'Unknown',
} satisfies Record<FindingOverlayTier, string>

const CLUSTER_DIMENSION_LABELS = {
  entry_point: 'Entry point',
  actor: 'Actor',
  trust_boundary: 'Trust boundary',
  sensitive_asset: 'Sensitive asset',
  vulnerability_class: 'Vulnerability class',
  unknown: 'Unknown',
} satisfies Record<FindingOverlayCluster['dimension'], string>

// One icon per dimension. Chosen to match the everyday reading of the label rather than a Pixee-
// specific glyph set: doors for entry points, people for actors, shields for trust boundaries,
// locks for sensitive assets, bugs for vulnerability classes, and a question mark for an axis this
// build does not recognise. Neutral tint so the icon reads as a category marker rather than a status.
const CLUSTER_DIMENSION_ICONS = {
  entry_point: DoorIcon,
  actor: UserIcon,
  trust_boundary: ShieldCheckIcon,
  sensitive_asset: LockIcon,
  vulnerability_class: BugIcon,
  unknown: QuestionIcon,
} satisfies Record<FindingOverlayCluster['dimension'], PhosphorIcon>

export type FindingOverlaysTabProps = {
  onSwitchToThreatModel: () => unknown
}

export const FindingOverlaysTab: React.FC<FindingOverlaysTabProps> = ({ onSwitchToThreatModel }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const queryClient = useQueryClient()

  const { data: threatModel, isLoading: isThreatModelLoading } = useGetRepositoryThreatModel({ repositoryId })
  const { data: findingOverlays, isLoading: isFindingOverlaysLoading } = useGetRepositoryFindingOverlays({
    repositoryId,
  })

  // `unknown` is an in-flight status, not a terminal one, so the stream gate reads it through the
  // shared helper — the same one the poll gate in `api-hooks.ts` uses. Gating on `=== 'processing'`
  // here would leave an indeterminate run with no live channel and no poll.
  const isInFlight = !!findingOverlays && isInFlightGenerationStatus(findingOverlays.status)
  const stream = useFindingOverlaysStream(isInFlight ? repositoryId : undefined)
  const lastStreamEvent = stream.events.at(-1)
  const firstStreamEvent = stream.events.at(0)

  useEffect(() => {
    if (lastStreamEvent && lastStreamEvent.status !== 'processing') {
      invalidateGetRepositoryFindingOverlays(queryClient, { repositoryId })
    }
  }, [lastStreamEvent, queryClient, repositoryId])

  // Content is only fetched once we know a completed run exists — the `_links.content` HAL link is
  // the source of truth (present whenever any run has ever completed, per spec §6). While a new
  // run is `processing`, the previous content stays renderable.
  const hasContentLink = !!findingOverlays?._links.content
  const { data: content, isLoading: isContentLoading } = useGetRepositoryFindingOverlaysContent({
    repositoryId,
    generatedAt: findingOverlays?.generatedAt,
    enabled: hasContentLink,
  })

  const showGenerateError = useShowMutationError<typeof usePostRepositoryFindingOverlays>({
    prefix: 'Failed to start scan prioritization',
  })
  const generateMutation = usePostRepositoryFindingOverlays({
    onSuccess: () => invalidateGetRepositoryFindingOverlays(queryClient, { repositoryId }),
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  const onGenerate = useCallback(
    () => generateMutation.mutate({ repositoryId }),
    [generateMutation.mutate, repositoryId]
  )

  const isThreatModelReady = !!threatModel?._links.content

  if (isFindingOverlaysLoading || isThreatModelLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading scan prioritization" />
      </div>
    )
  }

  if (!findingOverlays) {
    return isThreatModelReady ? (
      <FindingOverlaysEmptyState onGenerate={onGenerate} isStarting={isStarting} />
    ) : (
      <FindingOverlaysPrerequisiteChecklist
        isThreatModelReady={isThreatModelReady}
        onGenerateThreatModel={onSwitchToThreatModel}
      />
    )
  }

  const currentPhase = toSupportedPhase(lastStreamEvent?.phase ?? findingOverlays.phase)
  const startedAt = firstStreamEvent?.timestamp
  const hasContent = !!content

  return (
    <div className={styles.container}>
      {/* `unknown` renders as in-flight for the same reason it gates the stream: the run has not
          reached a terminal status, so the alternative is showing a terminal one it never reported. */}
      {match(findingOverlays.status)
        .with('processing', 'unknown', () =>
          hasContent ? (
            <>
              <FindingOverlaysRegeneratingBanner
                phase={currentPhase}
                requestedBy={findingOverlays.requestedBy}
                startedAt={startedAt}
              />
              <FindingOverlayContent content={content} isDimmed={true} />
            </>
          ) : (
            <FindingOverlaysGeneratingState
              phase={currentPhase}
              requestedBy={findingOverlays.requestedBy}
              startedAt={startedAt}
            />
          )
        )
        .with('failed', () => (
          <>
            <FindingOverlaysFailedBanner
              failureReason={findingOverlays.failureReason}
              onRetry={onGenerate}
              isRetrying={isStarting}
              canRetry={isThreatModelReady}
            />
            {hasContent && <FindingOverlayContent content={content} />}
          </>
        ))
        .with('completed', () => (
          <>
            <FindingOverlaysMetaRow
              generatedAt={findingOverlays.generatedAt}
              requestedBy={findingOverlays.requestedBy}
              summary={findingOverlays.summary}
              onRegenerate={onGenerate}
              isStarting={isStarting}
              canRegenerate={isThreatModelReady}
            />
            {/* A completed run whose content query has settled with nothing is not still loading — the
                fetch failed, or the document the HAL link promised is gone. Name that, the way the
                threat-model tab and the scorecard payload state do, rather than spinning forever on
                content that is never going to arrive. */}
            {match({ content, isContentLoading })
              .with({ content: P.nonNullable }, ({ content: loadedContent }) => (
                <FindingOverlayContent content={loadedContent} />
              ))
              .with({ isContentLoading: true }, () => <FindingOverlaysLoadingContent />)
              .otherwise(() => (
                <FindingOverlaysUnavailableContent />
              ))}
          </>
        ))
        .exhaustive()}
    </div>
  )
}

// #region Prerequisite checklist

type FindingOverlaysPrerequisiteChecklistProps = {
  isThreatModelReady: boolean
  onGenerateThreatModel: () => unknown
}

const FindingOverlaysPrerequisiteChecklist: React.FC<FindingOverlaysPrerequisiteChecklistProps> = ({
  isThreatModelReady,
  onGenerateThreatModel,
}) => (
  <div className={styles.checklist}>
    <div className={styles.checklistIntro}>
      <p className={styles.checklistTitle}>Scan prioritization needs a little setup</p>
      <p className={styles.checklistDescription}>
        Scan prioritization ranks and clusters this repository's triaged findings against its threat model. Complete the
        prerequisite below to enable generation.
      </p>
    </div>
    <ul className={styles.checklistItems}>
      <li
        className={styles.checklistItem}
        aria-label={`Threat model generated: ${isThreatModelReady ? 'met' : 'not met'}`}
      >
        <span className={styles.checklistIndicator({ state: isThreatModelReady ? 'met' : 'unmet' })} aria-hidden="true">
          {isThreatModelReady && <CheckIcon size={12} weight="bold" />}
        </span>
        <div className={styles.checklistItemBody}>
          <p className={styles.checklistItemLabel({ state: isThreatModelReady ? 'met' : 'unmet' })}>
            Threat model generated
          </p>
          {isThreatModelReady ? (
            <p className={styles.checklistItemMet}>A completed threat model is available.</p>
          ) : (
            <button className={styles.checklistAction} onClick={onGenerateThreatModel}>
              Generate a threat model
            </button>
          )}
        </div>
      </li>
    </ul>
  </div>
)

// #endregion

// #region Empty / loading states

type FindingOverlaysEmptyStateProps = {
  onGenerate: () => unknown
  isStarting: boolean
}

const FindingOverlaysEmptyState: React.FC<FindingOverlaysEmptyStateProps> = ({ onGenerate, isStarting }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No prioritization yet</p>
    <p className={styles.emptyStateDescription}>
      Rank and cluster this repository's triaged findings against its threat model. Generation usually takes a minute or
      two.
    </p>
    <DefaultButton buttonType="primary" state={isStarting ? 'loading' : 'default'} onClick={onGenerate}>
      Generate prioritization
    </DefaultButton>
  </div>
)

const FindingOverlaysLoadingContent: React.FC = () => (
  <div className={styles.loadingState}>
    <Spinner label="Loading prioritization content" />
  </div>
)

/** Reuses the empty state's panel: same shape of news — the run exists, its payload does not. */
const FindingOverlaysUnavailableContent: React.FC = () => (
  <div className={styles.emptyState} data-testid="finding-overlays-content-unavailable">
    <p className={styles.emptyStateTitle}>Prioritization content is unavailable</p>
    <p className={styles.emptyStateDescription}>
      The latest run completed but its content couldn&apos;t be loaded. Regenerating usually resolves it.
    </p>
  </div>
)

// #endregion

// #region Generation progress

type FindingOverlaysGeneratingStateProps = {
  phase: SupportedFindingOverlayPhase
  requestedBy?: string
  startedAt?: string
}

const FindingOverlaysGeneratingState: React.FC<FindingOverlaysGeneratingStateProps> = ({
  phase,
  requestedBy,
  startedAt,
}) => (
  <div className={styles.generatingState}>
    <PhaseStepper
      phases={FINDING_OVERLAYS_PHASES}
      labels={PHASE_LABELS}
      currentPhase={phase}
      currentColor={mappedColors.primary.pulse}
    />
    <div className={styles.generatingMeta}>
      <p className={styles.generatingTitle}>
        <Spinner size="16" label="Generating prioritization" />
        Generating prioritization
      </p>
      <p className={styles.metaText}>
        {joinMetaSegments([
          requestedBy && `Requested by ${requestedBy}`,
          startedAt && `Started ${formatRelativeTime(startedAt)}`,
        ])}
      </p>
      <p className={styles.hintText}>
        Usually takes a minute or two. You can leave this page — generation continues in the background.
      </p>
    </div>
  </div>
)

type FindingOverlaysRegeneratingBannerProps = {
  phase: SupportedFindingOverlayPhase
  requestedBy?: string
  startedAt?: string
}

const FindingOverlaysRegeneratingBanner: React.FC<FindingOverlaysRegeneratingBannerProps> = ({
  phase,
  requestedBy,
  startedAt,
}) => (
  <div className={styles.progressBanner} role="status" aria-label="Regenerating prioritization">
    <p className={styles.metaText}>
      <Spinner size="16" />
      {joinMetaSegments([
        `Regenerating — ${PHASE_LABELS[phase]} (${FINDING_OVERLAYS_PHASES.indexOf(phase) + 1}/${FINDING_OVERLAYS_PHASES.length})`,
        requestedBy && `requested by ${requestedBy}`,
        startedAt && `started ${formatRelativeTime(startedAt)}`,
      ])}
    </p>
  </div>
)

type FindingOverlaysFailedBannerProps = {
  failureReason: string | undefined
  onRetry: () => unknown
  isRetrying: boolean
  canRetry: boolean
}

const FindingOverlaysFailedBanner: React.FC<FindingOverlaysFailedBannerProps> = ({
  failureReason,
  onRetry,
  isRetrying,
  canRetry,
}) => (
  // Mirrors ThreatModelFailedBanner (post ISS-8359): icon in the title, prose beneath, ghost retry
  // button. Same colour vocabulary as the rest of the recent threat-model work.
  <div className={styles.failedState} role="alert">
    <p className={styles.failedStateTitle}>
      <WarningCircleIcon size={20} weight="fill" className={styles.failedStateIcon} />
      Scan prioritization failed
    </p>
    <p className={styles.failedStateDescription}>
      {failureReason ?? 'Something went wrong while generating the prioritization.'}
    </p>
    <GhostButton
      size="small"
      state={isRetrying || !canRetry ? 'disabled' : 'default'}
      icon={<ArrowClockwiseIcon size={14} />}
      onClick={onRetry}
    >
      Try again
    </GhostButton>
  </div>
)

type FindingOverlaysMetaRowProps = {
  generatedAt: string | undefined
  requestedBy?: string
  summary?: FindingOverlaySummary
  onRegenerate: () => unknown
  isStarting: boolean
  canRegenerate: boolean
}

const FindingOverlaysMetaRow: React.FC<FindingOverlaysMetaRowProps> = ({
  generatedAt,
  requestedBy,
  summary,
  onRegenerate,
  isStarting,
  canRegenerate,
}) => (
  <div className={styles.metaRow}>
    <p className={styles.metaText}>
      {joinMetaSegments([
        generatedAt && `Generated ${formatDate({ date: generatedAt, type: 'mm/dd/yyyy-hh:mm' })}`,
        summary && `${summary.priorityCount} ${pluralize(summary.priorityCount, 'priority', 'priorities')}`,
        summary && `${summary.clusterCount} ${pluralize(summary.clusterCount, 'cluster', 'clusters')}`,
        requestedBy && `requested by ${requestedBy}`,
      ])}
    </p>
    <DefaultButton
      buttonType="secondary"
      size="small"
      state={isStarting ? 'loading' : canRegenerate ? 'default' : 'disabled'}
      onClick={onRegenerate}
    >
      Regenerate
    </DefaultButton>
  </div>
)

// #endregion

// #region Content sections

type FindingOverlayContentProps = {
  content: FindingOverlayResponse
  isDimmed?: boolean
}

const FindingOverlayContent: React.FC<FindingOverlayContentProps> = ({ content, isDimmed = false }) => {
  // Opt-in, like every other filter in the app: an empty selection shows everything, and the button
  // narrows from there rather than starting narrowed.
  const [selectedTiers, setSelectedTiers] = useState<FindingOverlayTier[]>([])
  const counts = countByTier(content.priorities)

  return (
    <section
      aria-label="Scan prioritization"
      className={isDimmed ? styles.contentDimmed : styles.content}
      data-testid="finding-overlay-content"
    >
      <PrioritizationSummaryBanner
        narrative={content.narrative}
        priorities={content.priorities}
        counts={counts}
        selectedTiers={selectedTiers}
        onToggleTier={tier => setSelectedTiers(toggleTier(selectedTiers, tier))}
      />
      <PriorityList
        priorities={content.priorities}
        counts={counts}
        selectedTiers={selectedTiers}
        onChangeSelectedTiers={setSelectedTiers}
      />
      <ClusterList clusters={content.clusters} priorities={content.priorities} selectedTiers={selectedTiers} />
      <AssumptionsFooter assumptions={content.assumptions} />
    </section>
  )
}

const toggleTier = (selected: FindingOverlayTier[], tier: FindingOverlayTier): FindingOverlayTier[] =>
  selected.includes(tier) ? selected.filter(entry => entry !== tier) : [...selected, tier]

const countByTier = (priorities: FindingOverlayPriority[]): Record<FindingOverlayTier, number> => {
  const counts = Object.fromEntries(TIER_ORDER.map(tier => [tier, 0])) as Record<FindingOverlayTier, number>
  for (const priority of priorities) {
    counts[priority.tier] += 1
  }
  return counts
}

type PrioritizationSummaryBannerProps = {
  narrative: string
  priorities: FindingOverlayPriority[]
  counts: Record<FindingOverlayTier, number>
  selectedTiers: FindingOverlayTier[]
  onToggleTier: (tier: FindingOverlayTier) => unknown
}

const PrioritizationSummaryBanner: React.FC<PrioritizationSummaryBannerProps> = ({
  narrative,
  priorities,
  counts,
  selectedTiers,
  onToggleTier,
}) => {
  const total = priorities.length

  return (
    <section aria-label="Prioritization summary" className={styles.summaryBanner} data-testid="finding-overlay-summary">
      <div className={styles.summaryVisual}>
        <p className={styles.summaryVisualHeadline}>
          {total} {pluralize(total, 'finding', 'findings')} prioritized
        </p>
        <PriorityCompositionBar counts={counts} selectedTiers={selectedTiers} onSegmentClick={onToggleTier} />
      </div>
      <div className={styles.summaryText}>
        <RenderedMarkdown markdown={narrative} />
      </div>
    </section>
  )
}

type PriorityListProps = {
  priorities: FindingOverlayPriority[]
  counts: Record<FindingOverlayTier, number>
  selectedTiers: FindingOverlayTier[]
  onChangeSelectedTiers: (tiers: FindingOverlayTier[]) => unknown
}

const PriorityList: React.FC<PriorityListProps> = ({ priorities, counts, selectedTiers, onChangeSelectedTiers }) => {
  if (priorities.length === 0) {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Findings</h3>
        <p className={styles.emptySectionText}>No findings were prioritized in this overlay.</p>
      </div>
    )
  }

  // One flat, score-ordered grid: the tier is already on every card's score tag, so grouping repeated
  // the same word twice per card and pushed low-scoring criticals above high-scoring highs.
  const shown = priorities
    .filter(priority => selectedTiers.length === 0 || selectedTiers.includes(priority.tier))
    .sort((a, b) => b.score - a.score)

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Findings</h3>
        <SeverityFilter counts={counts} selected={selectedTiers} onChange={onChangeSelectedTiers} />
      </div>
      {shown.length === 0 ? (
        <p className={styles.emptySectionText}>No findings match the selected severities.</p>
      ) : (
        <ul className={styles.priorityGrid}>
          {shown.map(priority => (
            <PriorityCard key={priority.findingId} priority={priority} />
          ))}
        </ul>
      )}
    </div>
  )
}

type SeverityFilterProps = {
  counts: Record<FindingOverlayTier, number>
  selected: FindingOverlayTier[]
  onChange: (tiers: FindingOverlayTier[]) => unknown
}

const SeverityFilter: React.FC<SeverityFilterProps> = ({ counts, selected, onChange }) => {
  // Only offer severities this overlay actually produced; a checkbox that can only ever return zero
  // cards is noise.
  const available = TIER_ORDER.filter(tier => counts[tier] > 0)

  const filters: Filter[] = available.map(tier => ({
    type: 'checkbox',
    id: tier,
    text: TIER_LABELS[tier],
    count: counts[tier],
    value: selected.includes(tier),
    onToggle: () => onChange(toggleTier(selected, tier)),
  }))

  return (
    <DropdownFilter
      label="Filter by severity"
      align="end"
      filterElements={[
        {
          type: 'group',
          id: 'severity',
          text: 'Severity',
          filters,
          onToggleGroup: () => onChange(selected.length > 0 ? [] : available),
        },
      ]}
      isAnyNonDefaultFilterActive={selected.length > 0}
      onToggleAllFiltersOff={() => onChange([])}
    />
  )
}

const PriorityCard: React.FC<{ priority: FindingOverlayPriority }> = ({ priority }) => {
  const scanRoute = scanRouteFromLinks(priority._links)

  return (
    <li className={styles.priorityCard}>
      <div className={styles.priorityCardHeader}>
        <LinkedFindingChip
          findingId={priority.findingId}
          link={scanRoute}
          tool={priority.tool}
          rule={priority.rule}
          path={priority.path}
          severity={priority.severity}
        />
        <span className={styles.priorityScoreTag({ tier: priority.tier })} data-tier={priority.tier}>
          {priority.score} · {TIER_LABELS[priority.tier]}
        </span>
      </div>
      {priority.rationale && <p className={styles.priorityRationale}>{priority.rationale}</p>}
      {scanRoute && (
        <div className={styles.priorityCardFooter}>
          <Link to={scanRoute} className={styles.viewInScanLink}>
            View in scan
            <ArrowUpRightIcon size={13} weight="bold" aria-hidden="true" />
          </Link>
        </div>
      )}
    </li>
  )
}

type ClusterListProps = {
  clusters: FindingOverlayCluster[]
  priorities: FindingOverlayPriority[]
  selectedTiers: FindingOverlayTier[]
}

const ClusterList: React.FC<ClusterListProps> = ({ clusters, priorities, selectedTiers }) => {
  if (clusters.length === 0) {
    // Per spec §7: an empty clusters array on a completed run is valid — no meaningful grouping
    // was found. Suppress the section entirely rather than showing a "no clusters" bark.
    return null
  }

  const shown = filterClustersByTier({ clusters, priorities, selectedTiers })

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Clusters</h3>
      {shown.length === 0 ? (
        <p className={styles.emptySectionText}>No clusters contain findings at the selected severities.</p>
      ) : (
        <ul className={styles.clusterList}>
          {shown.map(cluster => (
            <ClusterCard key={`${cluster.dimension}:${cluster.subjectName}:${cluster.name}`} cluster={cluster} />
          ))}
        </ul>
      )}
    </div>
  )
}

type FilterClustersInput = {
  clusters: FindingOverlayCluster[]
  priorities: FindingOverlayPriority[]
  selectedTiers: FindingOverlayTier[]
}

/**
 * Narrows which clusters are shown, never which chips are inside one.
 *
 * A cluster's claim is "these findings converge here"; dropping chips out of it would leave a
 * narrower claim on screen than the model actually made. So the whole cluster stays or goes.
 *
 * Cluster findings carry the source tool's free-form `severity` string, not the prioritization
 * `tier` the filter speaks in, so the tier is recovered by `findingId` from the priorities list. A
 * chip with no matching priority has no recoverable tier — that is "unknown", not "no match", so it
 * keeps its cluster rather than silently hiding a grouping we cannot classify.
 */
const filterClustersByTier = ({
  clusters,
  priorities,
  selectedTiers,
}: FilterClustersInput): FindingOverlayCluster[] => {
  if (selectedTiers.length === 0) {
    return clusters
  }
  const tierByFindingId = new Map(priorities.map(priority => [priority.findingId, priority.tier]))

  return clusters.filter(cluster =>
    cluster.findings.some(finding => {
      const tier = tierByFindingId.get(finding.findingId)
      return tier === undefined || selectedTiers.includes(tier)
    })
  )
}

const ClusterCard: React.FC<{ cluster: FindingOverlayCluster }> = ({ cluster }) => {
  const DimensionIcon = CLUSTER_DIMENSION_ICONS[cluster.dimension]
  return (
    <li className={styles.clusterCard}>
      <div className={styles.clusterHeader}>
        <div className={styles.clusterHeaderTitle}>
          <span className={styles.clusterDimensionIcon} aria-hidden="true">
            <DimensionIcon size={16} weight="light" />
          </span>
          <p className={styles.clusterName}>{cluster.name}</p>
        </div>
        <span className={styles.clusterDimension}>
          {CLUSTER_DIMENSION_LABELS[cluster.dimension]} · {cluster.subjectName}
        </span>
      </div>
      {cluster.rationale && <p className={styles.clusterRationale}>{cluster.rationale}</p>}
      <div className={styles.chipRow}>
        {cluster.findings.map(finding => (
          <LinkedFindingChip
            key={finding.findingId}
            findingId={finding.findingId}
            link={scanRouteFromLinks(finding._links)}
            tool={finding.tool}
            rule={finding.rule}
            path={finding.path}
            severity={finding.severity}
          />
        ))}
      </div>
    </li>
  )
}

const AssumptionsFooter: React.FC<{ assumptions: string[] }> = ({ assumptions }) => {
  if (assumptions.length === 0) {
    return null
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Assumptions</h3>
      <div className={styles.assumptions}>
        <ul className={styles.assumptionsList}>
          {assumptions.map(assumption => (
            <li key={assumption} className={styles.assumptionItem}>
              {assumption}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// #endregion
